import React, { type FC, type SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement>;

const createIcon = (render: (props: IconProps) => React.ReactNode): FC<IconProps> => {
  const Icon: FC<IconProps> = (props) => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" {...props}>
      {render(props)}
    </svg>
  );
  return Icon;
};

export const Rewind10Icon = createIcon((props) => (
  <>
    <path
      d="M12 5V2L7 7l5 5V9c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46A7.93 7.93 0 0 0 20 15c0-4.42-3.58-8-8-8z"
      fill="currentColor"
    />
    <text x="12" y="16" textAnchor="middle" fill="currentColor" fontSize="7" fontWeight="700">
      10
    </text>
  </>
));

export const Forward10Icon = createIcon((props) => (
  <>
    <path
      d="M12 5V2l5 5-5 5V9c-3.31 0-6 2.69-6 6 0 1.01.25 1.97.7 2.8L4.24 17.26A7.93 7.93 0 0 1 4 15c0-4.42 3.58-8 8-8z"
      fill="currentColor"
    />
    <text x="12" y="16" textAnchor="middle" fill="currentColor" fontSize="7" fontWeight="700">
      10
    </text>
  </>
));

export const PlayIcon = createIcon(() => <path d="M8 5v14l11-7L8 5z" fill="currentColor" />);

export const PauseIcon = createIcon(() => (
  <>
    <rect x="6" y="5" width="4" height="14" rx="1" fill="currentColor" />
    <rect x="14" y="5" width="4" height="14" rx="1" fill="currentColor" />
  </>
));

export const FlagIcon = createIcon(() => (
  <path
    d="M14 4H5v16h2v-7h5.5c.83 0 1.5-.67 1.5-1.5S13.33 10 12.5 10H7V6h7V4z"
    fill="currentColor"
  />
));

export const ThumbsDownIcon = createIcon(() => (
  <path
    d="M15 3H6c-.83 0-1.54.5-1.84 1.22l-3.02 7.05c-.09.23-.14.47-.14.73v2c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L9.83 23l6.59-6.59c.36-.36.58-.86.58-1.41V5c0-1.1-.9-2-2-2zm4 0v12h4V3h-4z"
    fill="currentColor"
  />
));

export const SubtitlesIcon = createIcon(() => (
  <>
    <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M7 12h4M13 12h4M7 15h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </>
));

export const QualityIcon = createIcon(() => (
  <>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="12" cy="12" r="3" fill="currentColor" />
  </>
));

export const PlaybackSpeedIcon = createIcon(() => (
  <>
    <circle cx="12" cy="13" r="8" stroke="currentColor" strokeWidth="1.5" />
    <path d="M12 9v5l3 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M9 2h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M12 2v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </>
));

export const AudioTrackIcon = createIcon(() => (
  <>
    <path d="M9 18V5l10-2v13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="7" cy="18" r="3" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="17" cy="16" r="3" stroke="currentColor" strokeWidth="1.5" />
  </>
));

export const PipIcon = createIcon(() => (
  <>
    <rect x="3" y="5" width="14" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
    <rect x="11" y="11" width="10" height="8" rx="1.5" fill="currentColor" />
  </>
));

export const FullscreenIcon = createIcon(() => (
  <>
    <path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </>
));

export const VolumeHighIcon = createIcon(() => (
  <>
    <path d="M11 5L6 9H3v6h3l5 4V5z" fill="currentColor" />
    <path d="M15.54 8.46a5 5 0 0 1 0 7.07M18.36 5.64a9 9 0 0 1 0 12.72" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </>
));

export const VolumeMutedIcon = createIcon(() => (
  <>
    <path d="M11 5L6 9H3v6h3l5 4V5z" fill="currentColor" />
    <path d="M16 9l5 6M21 9l-5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </>
));

export const CheckIcon = createIcon(() => (
  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor" />
));

export const ChevronUpIcon = createIcon(() => (
  <path d="M7 14l5-5 5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
));

export const ChevronRightIcon = createIcon(() => (
  <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
));

export const formatTime = (seconds: number, options?: { forceHours?: boolean }): string => {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return options?.forceHours ? '00:00:00' : '00:00';
  }

  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hrs > 0 || options?.forceHours) {
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};
